import {yupResolver} from '@hookform/resolvers/yup';
import moment from 'moment';
import React, {useEffect, useState} from 'react';
import {FormProvider, SubmitHandler, useForm} from 'react-hook-form';
import {ScrollView, Text, View} from 'react-native';
import {Card, Divider} from 'react-native-paper';
import {useSelector} from 'react-redux';
import * as yup from 'yup';
import {ShowToast} from '../Common/common';
import BTButton from '../Components/BTButton';
import BTCheckbox from '../Components/BTCheckbox';
import BTComboBox from '../Components/BTComboBox';
import {Col, Row} from '../Components/BTGrid';
import {TextInput} from '../Components/BTTextInput';
import {
  useGetFOECheckinsQuery,
  useGetFOEOccRoomsQuery,
} from '../redux/services/foecheckinApi';
import {useInsertFOEPostingsMutation} from '../redux/services/foepostingsApi';
import {useGetFOMHeadsQuery} from '../redux/services/fomheadApi';
import {RootState} from '../redux/store';
import styles from '../styles/FOStyles';

import T from '../Components/T';
import { tp } from '../utils/translateText';
const validationSchema = yup.object({
  ps_checkin: yup.number().min(1, 'Folio is required').typeError('Required'),
  ps_head: yup.number().min(1, 'Head is required').typeError('Required'),
  ps_rate: yup.number().required('Amount is required').typeError('Required'),
});

const PostingForm = ({navigation, route}: UniversalProps) => {
  const gdata = useSelector((state: RootState) => state.gdata);
  const cid = gdata.company;

  const {data: checkinData} = useGetFOECheckinsQuery(cid);
  const {data: headData} = useGetFOMHeadsQuery(cid);
  const {data: occData} = useGetFOEOccRoomsQuery({cid: cid, coid: 0});
  const [insertPosting] = useInsertFOEPostingsMutation();

  const [isSaving, setIsSaving] = useState(false);

  const checkins = checkinData?.map(item => ({
    label: `${item.ci_no} - ${item.ab_name}`,
    id: item.ci_id,
  }));

  let occs: any = occData?.map((value: any) => {
    return {
      label: value.rm_name + ' - ' + value.ab_name,
      id: value.cd_room,
      value: value,
    };
  });

  const heads = headData?.map(item => ({
    label: item.hd_name,
    id: item.hd_id,
    value: item,
  }));

  const initialValues = {
    ps_id: 0,
    ps_checkin: 0,
    ps_room: 0,
    ps_curroom: 0,
    ps_head: 0,
    ps_rate: 0,
    ps_tax1: 0,
    ps_tax1per: 0,
    ps_tax1amt: 0,
    ps_tax2: 0,
    ps_tax2per: 0,
    ps_tax2amt: 0,
    ps_tax3: 0,
    ps_tax3per: 0,
    ps_tax3amt: 0,
    ps_discper: 0,
    ps_discamt: 0,
    ps_net: 0,
    ps_narr: '',
    ps_flag: '1',
    ps_module: 0,
    ps_date: moment(gdata.today).format('YYYY/MM/DD HH:mm:ss'),
    ps_company: cid,
    ps_uid: gdata.user,
    ps_muid: gdata.user,
    ps_cdt: moment().format('YYYY/MM/DD HH:mm:ss'),
    ps_mdt: moment().format('YYYY/MM/DD HH:mm:ss'),
  };

  const methods = useForm<any>({
    defaultValues: initialValues,
    resolver: yupResolver(validationSchema),
  });

  const {getValues, setValue} = methods;

  useEffect(() => {
    // Full reset (not per-field setValue) so head/rate/discount/remarks left
    // over from a previous room's unsaved posting don't carry over when this
    // same mounted screen instance is reused for a different room via
    // navigation.navigate('Posting', {room}). `checkins`/`occs` were
    // previously in the deps array but are rebuilt via .map() every render
    // (new reference each time) and aren't read in this effect - it only
    // caused this effect to refire on unrelated re-renders, not just on room
    // changes.
    if (route.params?.room) {
      const selectedRoom = occData?.find(
        (v: any) => v.rm_id === Number(route.params.room),
      );
      methods.reset({
        ...initialValues,
        ps_room: Number(route.params.room),
        ps_curroom: Number(route.params.room),
        ps_checkin: selectedRoom ? selectedRoom.ci_id : 0,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route.params?.room, occData]);

  const calculateNetAmount = () => {
    const rate = Number(getValues('ps_rate')) || 0;
    const discAmt = (rate * (Number(getValues('ps_discper')) || 0)) / 100;

    const taxBase = rate - (discAmt || 0);
    const tax1Amt = (taxBase * (Number(getValues('ps_tax1per')) || 0)) / 100;
    const tax2Amt = (taxBase * (Number(getValues('ps_tax2per')) || 0)) / 100;
    const tax3Amt = (taxBase * (Number(getValues('ps_tax3per')) || 0)) / 100;

    const net = taxBase + tax1Amt + tax2Amt + tax3Amt;

    setValue('ps_discamt', discAmt.toFixed(2));
    setValue('ps_tax1amt', tax1Amt.toFixed(2));
    setValue('ps_tax2amt', tax2Amt.toFixed(2));
    setValue('ps_tax3amt', tax3Amt.toFixed(2));
    setValue('ps_net', net.toFixed(2));
  };

  const onHeadChange = (selected: any) => {
    setValue('ps_head', selected);
    setValue(
      'ps_tax1per',
      headData?.find(v => v.hd_id === selected)?.hd_tax1per || 0,
    );
    setValue(
      'ps_tax2per',
      headData?.find(v => v.hd_id === selected)?.hd_tax2per || 0,
    );
    setValue(
      'ps_tax3per',
      headData?.find(v => v.hd_id === selected)?.hd_tax3per || 0,
    );

    calculateNetAmount();
  };

  const onSubmit: SubmitHandler<any> = async values => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      await insertPosting(values).unwrap();
      ShowToast('Posting Saved');

      methods.reset(initialValues);
      navigation.navigate('StatusScreen');
    } catch (error: any) {
      ShowToast(error?.data?.message || error?.message || 'Failed to save posting', 'error');
    }
    setIsSaving(false);
  };

  return (
    <View style={{ flex: 1, backgroundColor: '#f5f5f5' }}>
      <ScrollView
        contentContainerStyle={{
          padding: 8,
          paddingBottom: 80,
          flexGrow: 1,
          backgroundColor: '#f5f5f5',
        }}
        keyboardShouldPersistTaps="handled">
      <View style={styles.header}>
        <Text style={styles.title}><T>Post Charges</T></Text>
      </View>
      <FormProvider {...methods}>
        <Card style={styles.cardfill}>
          <Card.Content>
            <Row>
              <Col>
                <BTComboBox
                  name="ps_checkin"
                  label="Folio No"
                  data={checkins}
                  placeholder={tp("Select Folio")}
                  editable={false}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <BTComboBox
                  name="ps_room"
                  label="Room no"
                  data={occs}
                  placeholder={tp("Select Room")}
                  editable={false}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <BTComboBox
                  name="ps_head"
                  label="Head"
                  data={heads}
                  placeholder={tp("Select Head")}
                  onValueChange={onHeadChange}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <TextInput
                  name="ps_rate"
                  label="Amount"
                  keyboardType="numeric"
                  textAlign="right"
                  onChangeText={calculateNetAmount}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <TextInput
                  name="ps_discper"
                  label="Disc %"
                  keyboardType="numeric"
                  onChangeText={calculateNetAmount}
                  textAlign="right"
                />
              </Col>
              <Col>
                <TextInput
                  name="ps_discamt"
                  label="Disc Amt"
                  keyboardType="numeric"
                  textAlign="right"
                  editable={false}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <TextInput
                  name="ps_tax1amt"
                  label="CGST"
                  keyboardType="numeric"
                  textAlign="right"
                  editable={false}
                />
              </Col>
              <Col>
                <TextInput
                  name="ps_tax2amt"
                  label="SGST"
                  keyboardType="numeric"
                  textAlign="right"
                  editable={false}
                />
              </Col>
              <Col>
                <TextInput
                  name="ps_tax3amt"
                  label="Other Tax"
                  keyboardType="numeric"
                  textAlign="right"
                  editable={false}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <TextInput
                  name="ps_net"
                  label="Net Amount"
                  keyboardType="numeric"
                  textAlign="right"
                  editable={false}
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <TextInput name="ps_narr" label="Remarks" />
              </Col>
            </Row>
            <Row>
              <Col>
                <BTCheckbox name="ps_flag" label="Active" />
              </Col>
            </Row>
            <Divider />
            <Row>
              <Col>
                <BTButton
                  title="Save"
                  onPress={methods.handleSubmit(onSubmit)}
                  onLongPress={() => {}}
                  color="#146C94"
                  disabled={isSaving}
                />
              </Col>
              <Col>
                <BTButton
                  title="Cancel"
                  onPress={() => {
                    methods.reset(initialValues);
                    navigation.navigate('StatusScreen');
                  }}
                  onLongPress={() => {}}
                  color="#146C94"
                />
              </Col>
            </Row>
          </Card.Content>
        </Card>
      </FormProvider>
    </ScrollView>
  </View>
);
};

export default PostingForm;

